import { CANONICAL_ORIGIN } from "@/lib/config";
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from "@/lib/i18n-locales";

const hrefLangMap: Record<SupportedLocale, string> = {
  pt: "pt",
  en: "en",
  es: "es",
  fr: "fr",
  it: "it",
  de: "de",
  ro: "ro",
};

const openGraphLocaleMap: Record<SupportedLocale, string> = {
  pt: "pt_BR",
  en: "en_US",
  es: "es_ES",
  fr: "fr_FR",
  it: "it_IT",
  de: "de_DE",
  ro: "ro_RO",
};

function normalizePath(path: string): string {
  if (!path || path === "/") return "/";
  const withLeadingSlash = path.startsWith("/") ? path : `/${path}`;
  return withLeadingSlash.endsWith("/")
    ? withLeadingSlash.slice(0, -1)
    : withLeadingSlash;
}

export function getLocalizedPath(locale: string, path: string): string {
  const normalizedPath = normalizePath(path);
  if (locale === DEFAULT_LOCALE) return normalizedPath;
  if (normalizedPath === "/") return `/${locale}`;
  return `/${locale}${normalizedPath}`;
}

export function getLocalizedCanonical(locale: string, path: string): string {
  return `${CANONICAL_ORIGIN}${getLocalizedPath(locale, path)}`;
}

export function getLocaleAlternates(path: string): Record<string, string> {
  const alternates = SUPPORTED_LOCALES.reduce<Record<string, string>>(
    (acc, locale) => {
      acc[hrefLangMap[locale]] = getLocalizedCanonical(locale, path);
      return acc;
    },
    {},
  );

  alternates["x-default"] = getLocalizedCanonical(DEFAULT_LOCALE, path);
  return alternates;
}

export function getOpenGraphLocale(locale: string): string {
  const normalizedLocale = (
    SUPPORTED_LOCALES.includes(locale as SupportedLocale)
      ? locale
      : DEFAULT_LOCALE
  ) as SupportedLocale;
  return openGraphLocaleMap[normalizedLocale];
}
